import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import AddIcon from '@mui/icons-material/Add'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import { EMAIL_RE, SUPORTE_EMAILS_MAX, type PlatformSettings } from '../settingsModel'
import { PERM } from '../platformRoles'
import { usePlatformAuth } from '../context/PlatformAuthContext'

/** Ordem estável para comparar duas listas de emails (detecção de alterações). */
const chave = (emails: string[]) => [...emails].sort().join('|')

export default function PlatformSettings() {
    const { notify } = useNotification()
    const { hasPerm } = usePlatformAuth()
    const podeGerir = hasPerm(PERM.SETTINGS_MANAGE)

    const [saved, setSaved] = useState<string[]>([])
    const [emails, setEmails] = useState<string[]>([])
    const [entrada, setEntrada] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        let cancelled = false
        api.get<PlatformSettings>('/api/admin/settings')
            .then((r) => {
                if (cancelled) return
                const lista = r.data.suporteEmails ?? []
                setSaved(lista)
                setEmails(lista)
            })
            .catch(() => { if (!cancelled) setError('Não foi possível carregar as definições.') })
            .finally(() => { if (!cancelled) setIsLoading(false) })
        return () => { cancelled = true }
    }, [])

    const entradaLimpa = entrada.trim().toLowerCase()
    const entradaInvalida = entradaLimpa !== '' && !EMAIL_RE.test(entradaLimpa)
    const jaExiste = entradaLimpa !== '' && emails.includes(entradaLimpa)
    const dirty = useMemo(() => chave(saved) !== chave(emails), [saved, emails])

    const adicionar = () => {
        if (!entradaLimpa) return
        if (!EMAIL_RE.test(entradaLimpa)) { notify('Email inválido.', 'error'); return }
        if (emails.includes(entradaLimpa)) { setEntrada(''); return }
        if (emails.length >= SUPORTE_EMAILS_MAX) {
            notify(`No máximo ${SUPORTE_EMAILS_MAX} emails.`, 'warning')
            return
        }
        setEmails((prev) => [...prev, entradaLimpa])
        setEntrada('')
    }

    const remover = (email: string) => setEmails((prev) => prev.filter((e) => e !== email))

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); adicionar() }
    }

    const guardar = async () => {
        setIsSaving(true)
        try {
            const r = await api.put<PlatformSettings>('/api/admin/settings', { suporteEmails: emails })
            const lista = r.data.suporteEmails ?? []
            setSaved(lista)
            setEmails(lista)
            notify('Definições guardadas.')
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível guardar as definições.', 'error')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Box>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Definições da plataforma</Typography>
                <Typography variant="body2" color="text.secondary">
                    Opções gerais da administração da plataforma.
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}
            {!isLoading && !podeGerir && (
                <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                    Só de leitura — não tem permissão para alterar as definições da plataforma.
                </Alert>
            )}

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : (
                <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid #E5E7EB', borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Emails de suporte</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        Sempre que um utilizador submeter um novo pedido de suporte, é enviado um aviso
                        para estes endereços. Sem nenhum definido, o aviso vai para o administrador da
                        plataforma por omissão.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            type="email"
                            label="Adicionar email"
                            placeholder="suporte@empresa.com"
                            value={entrada}
                            onChange={(e) => setEntrada(e.target.value)}
                            onKeyDown={onKeyDown}
                            disabled={!podeGerir}
                            error={entradaInvalida}
                            helperText={entradaInvalida ? 'Email inválido.' : jaExiste ? 'Já está na lista.' : ' '}
                            sx={{ minWidth: 280, flexGrow: 1, maxWidth: 420 }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={adicionar}
                            disabled={!podeGerir || entradaLimpa === '' || entradaInvalida || jaExiste}
                            sx={{ mt: 0.25 }}
                        >
                            Adicionar
                        </Button>
                    </Box>

                    <Box sx={{ mt: 1.5, minHeight: 40 }}>
                        {emails.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Nenhum email definido.
                            </Typography>
                        ) : (
                            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                {emails.map((email) => (
                                    <Chip
                                        key={email}
                                        label={email}
                                        onDelete={podeGerir ? () => remover(email) : undefined}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                            onClick={guardar}
                            disabled={!podeGerir || !dirty || isSaving}
                        >
                            Guardar
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    )
}
